import type { Game } from "@rally/rating";
import { DemoError } from "./demo.js";
import { supabase } from "./supabase.js";
import type { DrawFixture, FixtureSide, MatchStatus, TiePairing } from "./types.js";

const FIXTURE_COLUMNS =
  "id, event_id, round, sequence, pool, seed_pair, seed_pair_b, side_a_kind, side_a, side_a_from, " +
  "side_b_kind, side_b, side_b_from, match_id, " +
  "match:matches (id, status, games, winner, played_at, moves_ratings, match_participants (member_id, side))";

interface FixtureRow {
  id: string;
  event_id: string;
  round: number;
  sequence: number;
  pool: number | null;
  seed_pair: number[] | null;
  seed_pair_b: number[] | null;
  side_a_kind: FixtureSide["kind"];
  side_a: string[];
  side_a_from: string | null;
  side_b_kind: FixtureSide["kind"];
  side_b: string[];
  side_b_from: string | null;
  match_id: string | null;
  match: {
    id: string;
    status: MatchStatus;
    games: Game[];
    winner: "A" | "B";
    played_at: string;
    moves_ratings: boolean;
    match_participants: { member_id: string; side: "A" | "B" }[];
  } | null;
}

/**
 * Every fixture in this event's draw, in playing order.
 *
 * Names are resolved by the caller against the roster it already has, rather than embedded
 * here. A fixture holds a `uuid[]` rather than rows in a join table — the draw is written
 * and replaced whole, so an array is the honest shape — and PostgREST cannot embed through
 * one. Looking two ids up in a Map the page already built is cheaper than the join anyway.
 */
export async function fetchDraw(eventId: string): Promise<DrawFixture[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("fixtures")
    .select(FIXTURE_COLUMNS)
    .eq("event_id", eventId)
    .order("sequence");

  if (error) throw new Error(error.message);

  const rows = (data ?? []) as unknown as FixtureRow[];
  const sequenceOf = new Map(rows.map((row) => [row.id, row.sequence]));

  const side = (kind: FixtureSide["kind"], players: string[], from: string | null): FixtureSide => {
    if (kind === "players") return { kind, memberIds: players };
    if (kind === "winner") return { kind, of: (from && sequenceOf.get(from)) || 0 };
    return { kind: "bye" };
  };

  /**
   * Following "winner of #3" through to the four names on court.
   *
   * One pass in sequence order is enough: a slot only ever points at a lower sequence
   * number, which is asserted in bracket.test.ts and relied on by the insert in
   * `replace_draw`. So a fixture's feeders are always already resolved when it comes round.
   *
   * `fixture_winner` in the database does the same walk and is the one that decides whether
   * a score is allowed. This copy is for display — it puts the names on the fixture instead
   * of a promise about which match they come out of — and if the two ever disagreed, the
   * database would simply refuse the score.
   */
  const winnerOf = new Map<string, string[] | null>();
  const resolve = (
    kind: FixtureSide["kind"],
    players: string[],
    from: string | null,
  ): string[] | null => {
    if (kind === "players") return players;
    if (kind === "bye") return null;
    return (from && winnerOf.get(from)) || null;
  };

  return rows.map((row) => {
    const playersA = resolve(row.side_a_kind, row.side_a, row.side_a_from);
    const playersB = resolve(row.side_b_kind, row.side_b, row.side_b_from);
    const played = row.match && row.match.status !== "void" ? row.match : null;

    winnerOf.set(
      row.id,
      played
        ? played.match_participants.filter((p) => p.side === played.winner).map((p) => p.member_id)
        : // A walkover: nobody played, and whoever was drawn against the bye goes through.
          row.side_a_kind === "bye"
          ? playersB
          : row.side_b_kind === "bye"
            ? playersA
            : null,
    );

    return {
      id: row.id,
      round: row.round,
      sequence: row.sequence,
      pool: row.pool,
      seedPair: row.seed_pair,
      seedPairB: row.seed_pair_b,
      sideA: side(row.side_a_kind, row.side_a, row.side_a_from),
      sideB: side(row.side_b_kind, row.side_b, row.side_b_from),
      matchId: row.match_id,
      result: played
        ? {
            matchId: played.id,
            status: played.status,
            games: played.games,
            winner: played.winner,
            playedAt: played.played_at,
            movesRatings: played.moves_ratings,
          }
        : null,
      playersA,
      playersB,
    };
  });
}

/** The parts of a fixture that decide whether a score can be entered against it. */
export interface PlayableFixture {
  result?: { winner: "A" | "B" } | null;
  playersA?: string[] | null;
  playersB?: string[] | null;
}

/**
 * Both sides known and nothing scored yet — the two things `score_fixture` insists on.
 *
 * A bye has no players on one side and is never played; a knockout slot waiting on an
 * earlier round has none either, and the message for that one ("the match that feeds this
 * one has not been played yet") is better as an absent button than as an error.
 */
export const isPlayable = (fixture: PlayableFixture): boolean =>
  !fixture.result && (fixture.playersA?.length ?? 0) > 0 && (fixture.playersB?.length ?? 0) > 0;

/**
 * Enter the score of a fixture.
 *
 * The players are not sent. `score_fixture` reads them off the draw, so a score can only
 * ever be recorded against the people the draw says are playing — and the match it writes
 * carries the event, the round and the seed pair, which is what makes it countable as part
 * of the tournament rather than a casual game that happened to be at one.
 *
 * Organisers and players both, per requirements §5.3. Which of the two you are is the
 * database's question, not this screen's.
 */
export async function scoreFixture(
  fixtureId: string,
  games: Game[],
  playedAt?: string,
): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("score_fixture", {
    target_fixture: fixtureId,
    p_games: games,
    p_played_at: playedAt ?? new Date().toISOString(),
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/**
 * Change who is drawn to play one fixture.
 *
 * The injury replacement, mid-tournament: match three is on court, somebody rolls an ankle,
 * and the four matches they were drawn in for the rest of the evening need another name on
 * them. A redraw would throw away the three results already in; this changes one line.
 *
 * `edit_fixture` refuses a fixture that has been played, a knockout slot that is filled in by
 * an earlier result, and anybody who is not entered for the event — and audits the swap with
 * the reason. Organisers and admins only, which the database decides.
 */
export async function editFixture(
  fixtureId: string,
  sideA: string[],
  sideB: string[],
  reason: string,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("edit_fixture", {
    target_fixture: fixtureId,
    p_side_a: sideA,
    p_side_b: sideB,
    reason,
  });

  if (error) throw new Error(error.message);
}

/**
 * Put one more match on the end of the draw.
 *
 * The replacement who arrives at eight and is owed a game, the pair who sat out the round
 * that went wrong, the tie that finishes early with the hall still booked. Until this, the
 * only way to add a match was to redraw — which discards every result already in.
 *
 * It lands at the highest sequence plus one and moves nothing that exists. `add_fixture`
 * refuses a knockout, where a match on the end belongs to nothing.
 */
export async function addFixture(
  eventId: string,
  sideA: string[],
  sideB: string[],
  reason: string,
): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("add_fixture", {
    target_event: eventId,
    p_side_a: sideA,
    p_side_b: sideB,
    reason,
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/**
 * Take one match out of the draw.
 *
 * The sequence numbers of everything after it stay as they are — an organiser has been
 * calling this "match seven" all evening, and closing the gap would rename the one that is
 * about to be played.
 */
export async function removeFixture(fixtureId: string, reason: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("remove_fixture", {
    target_fixture: fixtureId,
    reason,
  });

  if (error) throw new Error(error.message);
}

export interface DrawnPair {
  names: string[];
  /** Mean of the two, or null when either partner is unrated. */
  rating: number | null;
}

/**
 * A fixture as the draw generator emits it, before anything is stored.
 *
 * The same shape as a stored one minus its id — `winner.of` is a sequence number in both,
 * because the package speaks sequences and `fetchDraw` translates the database's foreign key
 * back into one. So a preview and a saved draw render through identical code.
 */
export interface ProposedFixture {
  round: number;
  sequence: number;
  sideA: FixtureSide;
  sideB: FixtureSide;
}

export interface DrawResult {
  ok: true;
  /** True when nothing was written and this is only what the draw *would* be. */
  preview: boolean;
  fixtureCount: number;
  /** Every fixture, so a preview can be read rather than counted. */
  fixtures: ProposedFixture[];
  rounds: number;
  /** How many entrants had no rating in this discipline and so were seeded last. */
  unrated: number;
  /** Null for singles and team ties, neither of which pairs entrants up front. */
  strategy: "snake" | "balanced" | "random" | null;
  /**
   * The shuffle seed this draw was built from. Sending it back on confirm makes the server
   * re-derive the same teams, which is what lets a preview mean anything.
   */
  randomSeed: number;
  /**
   * Fixtures that start while one of their players is still on court from the one before.
   *
   * Only ever non-zero for a team tie, and sometimes unavoidable there — at the maximum
   * matches-per-player the fixture set leaves no clash-free order to find. Surfaced rather
   * than hidden because the organiser is the one who gets asked "why am I on again".
   */
  restViolations: number;
  /** Team tie only: which pairing this draw used. Null for every other format. */
  tiePairing?: TiePairing | null;
  /** Team tie only: the widest seed-sum difference in any fixture. Always 0 when Mirrored. */
  largestSeedGap?: number;
  pairs: DrawnPair[];
  /**
   * Mexicano only: which round this is. Every other format draws the whole event at once and
   * has nothing to say here.
   */
  round?: number;
  /**
   * Mexicano only: who is off this round, when the field does not divide by four. Member
   * ids — the screen has the roster and turns them into names.
   */
  sittingOut?: string[];
  /** Swiss only: how many rounds the event plays, this one included. */
  of?: number;
  /** Swiss only: who has the bye this round, or null. */
  bye?: string | null;
  /** Swiss only: pairs meeting again, when no round without a rematch exists. */
  rematches?: [string, string][];
}

/**
 * Ask the server to draw this event.
 *
 * Organiser and admin only, and the Edge Function is what says so — this is not gated by
 * hiding the button. Its refusals are written to be read by an organiser ("7 entrants cannot
 * be paired — add one more, or take one out of the draw"), so the message comes through
 * unaltered.
 *
 * With `preview`, the whole draw is computed and none of it stored — requirements §5.4 wants
 * the team ratings visible before anyone commits. Confirming then passes `randomSeed` back so
 * the server rebuilds the identical draw; the fixtures themselves are never sent from here,
 * because a client that could hand over a fixture list could hand over one it wrote itself.
 *
 * `matchesPerPlayer` is the team tie's `m`, and it belongs at draw time rather than only on
 * the event form: how many games each player gets is a decision about the length of the
 * evening, which is known when the teams are picked and the courts are booked, not when
 * somebody first typed the event's name. Committing writes it back to the event, so the
 * stored number always describes the draw that exists.
 */
export async function generateDraw(
  eventId: string,
  options: {
    preview?: boolean;
    randomSeed?: number;
    matchesPerPlayer?: number | null;
    pairing?: TiePairing;
  } = {},
): Promise<DrawResult> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.functions.invoke("generate-draw", {
    body: { eventId, ...options },
  });

  // A non-2xx from an Edge Function arrives as a FunctionsHttpError whose useful half is in
  // the response body, not in `error.message` — which says only "Edge Function returned a
  // non-2xx status code". Without digging the body out, an organiser who tries to redraw a
  // played event is told nothing at all.
  if (error) {
    const response = (error as { context?: Response }).context;
    const body = await response?.json().catch(() => null as { error?: string } | null);

    // 401 means the session is no longer good — usually an access token whose refresh was
    // refused, which is what happens when the account is signed out elsewhere. The function
    // says "invalid token", which is accurate and no help at all to somebody holding a
    // phone; this is the one error on this screen worth rewriting.
    if (response?.status === 401) {
      throw new Error("Your session has expired — sign in again, then try the draw.");
    }

    throw new Error(body?.error ?? error.message);
  }

  return data as DrawResult;
}
